import styled from "styled-components";

export const PageContainer = styled.div`
  padding: 24px;
  background: #fff;
  min-height: 100vh;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;

  .filter-item {
    min-width: 200px;
    flex: 1;
    max-width: 300px;

    .filter-label {
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: #666;
      font-weight: 500;
    }
  }

  .filter-actions {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }
`;

export const TableContainer = styled.div`
  .ant-table-wrapper {
    .ant-table {
      border-radius: 8px;
      overflow: hidden;
    }

    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 600;
    }

    .ant-table-row {
      cursor: pointer;

      &:hover {
        background: #f5f5f5;
      }
    }
  }
`;

export const StatusTag = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => props.$color}20;
  color: ${(props) => props.$color};
  border: 1px solid ${(props) => props.$color}40;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;

  .pagination-info {
    color: #666;
    font-size: 14px;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const ProgressBar = styled.div<{ $percentage: number; $hasFailed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .progress-bar-container {
    width: 100%;
    height: 8px;
    background: #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
  }

  .progress-bar-fill {
    height: 100%;
    background: ${(props) => (props.$hasFailed ? "#ff4d4f" : "#52c41a")};
    transition: width 0.3s ease;
  }

  .progress-bar-pending {
    height: 100%;
    background: #1890ff;
    transition: width 0.3s ease;
  }

  .progress-text {
    font-size: 12px;
    color: #666;
  }
`;

export const BatchSummaryCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
  }

  .summary-item {
    padding: 16px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #f0f0f0;

    .summary-label {
      font-size: 12px;
      color: #666;
      margin-bottom: 4px;
    }

    .summary-value {
      font-size: 24px;
      font-weight: 600;
      color: #1890ff;

      &.success {
        color: #52c41a;
      }

      &.error {
        color: #ff4d4f;
      }

      &.warning {
        color: #faad14;
      }
    }
  }
`;

export const SubmissionsSection = styled.div`
  margin-top: 24px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }
`;

export const ErrorList = styled.ul`
  margin: 0;
  padding: 0 0 0 16px;
  font-size: 12px;
  color: #ff4d4f;

  li {
    margin-bottom: 2px;
  }
`;

export const BackButton = styled.div`
  margin-bottom: 16px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #1890ff;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const ReportTypeTag = styled.span<{ $type: "patient" | "insurance" | "both" }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$type) {
      case "patient":
        return "#e6f7ff";
      case "insurance":
        return "#f6ffed";
      case "both":
        return "#fff7e6";
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case "patient":
        return "#1890ff";
      case "insurance":
        return "#52c41a";
      case "both":
        return "#fa8c16";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$type) {
        case "patient":
          return "#91d5ff";
        case "insurance":
          return "#b7eb8f";
        case "both":
          return "#ffd591";
      }
    }};
`;

export const ModalContent = styled.div`
  .retry-description {
    margin-bottom: 16px;
  }
`;

// Stats Dashboard
export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

export const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, ${props => props.$color || '#6366f1'}15 0%, ${props => props.$color || '#6366f1'}05 100%);
  border: 1px solid ${props => props.$color || '#6366f1'}30;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.$color || '#6366f1'}20;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${props => props.$color || '#6366f1'}20;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    color: ${props => props.$color || '#6366f1'};
    font-size: 18px;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 4px;
    line-height: 1;
  }

  .stat-label {
    font-size: 13px;
    color: #666;
    font-weight: 500;
  }
`;

// Progress Timeline
export const TimelineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  background: #fafafa;
  border-radius: 12px;
  margin-bottom: 24px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 80px;
    right: 80px;
    height: 3px;
    background: #e5e7eb;
    transform: translateY(-50%);
    z-index: 0;
  }
`;

export const TimelineStep = styled.div<{ $active: boolean; $completed: boolean; $failed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  background: #fafafa;
  padding: 0 12px;

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 8px;
    transition: all 0.3s ease;
    
    background: ${props => {
      if (props.$failed) return '#fff2f0';
      if (props.$completed) return '#f6ffed';
      if (props.$active) return '#e6f7ff';
      return '#f5f5f5';
    }};
    
    border: 2px solid ${props => {
      if (props.$failed) return '#ff4d4f';
      if (props.$completed) return '#52c41a';
      if (props.$active) return '#1890ff';
      return '#d9d9d9';
    }};
    
    color: ${props => {
      if (props.$failed) return '#ff4d4f';
      if (props.$completed) return '#52c41a';
      if (props.$active) return '#1890ff';
      return '#8c8c8c';
    }};
  }

  .step-label {
    font-size: 12px;
    font-weight: 500;
    color: ${props => {
      if (props.$failed) return '#ff4d4f';
      if (props.$completed || props.$active) return '#1a1a2e';
      return '#8c8c8c';
    }};
  }

  .step-time {
    font-size: 13px;
    font-weight: 600;
    color: ${props => {
      if (props.$failed) return '#ff4d4f';
      if (props.$completed || props.$active) return '#1a1a2e';
      return '#8c8c8c';
    }};
    margin-top: 4px;
  }

  .step-date {
    font-size: 11px;
    color: #8c8c8c;
    margin-top: 2px;
  }
`;

// Error Summary Section
export const ErrorSummaryContainer = styled.div`
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;

  .error-summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h4 {
      margin: 0;
      color: #cf1322;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .error-list {
    max-height: 200px;
    overflow-y: auto;
  }

  .error-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px;
    background: #fff;
    border-radius: 8px;
    margin-bottom: 8px;
    border: 1px solid #ffa39e;

    &:last-child {
      margin-bottom: 0;
    }

    .error-message {
      flex: 1;
      color: #434343;
      font-size: 13px;
      line-height: 1.5;
    }

    .error-count {
      background: #ff4d4f;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      margin-left: 12px;
      white-space: nowrap;
    }
  }
`;

// Export Actions
export const ExportActions = styled.div`
  display: flex;
  gap: 8px;
`;

// Bulk Selection
export const BulkActionsBar = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 16px;
  color: white;

  .selection-info {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
  }

  .bulk-actions {
    display: flex;
    gap: 8px;
  }
`;





